import { IonInput, IonItem, IonLabel, IonRange, IonSelect, IonSelectOption, IonText } from "@ionic/react";
import { Birthdate } from "./kyc-schema";
import { QuestionType, SurveyAnswers } from "./question-types";

interface QuestionProps {
  questionKey: keyof SurveyAnswers;
  question: QuestionType;
  value: any;
  onChange: (key: keyof SurveyAnswers, value: any) => void;
  editMode?: boolean;
}

export const Question: React.FC<QuestionProps> = ({ questionKey, question, value, onChange, editMode }) => {
  if (!editMode) {
    let displayValue = value;
    if (question.type === 'birthdate' && value) {
      const birthdate = value as Birthdate;
      displayValue = `${birthdate.month}/${birthdate.day}/${birthdate.year}`;
    }
    if (question.type === 'choiceNumber' && typeof value === 'number') {
      displayValue = `${Math.round(value * 100)}%`;
    }
    return (
      <IonItem>
        <IonLabel position="stacked">{question.question}</IonLabel>
        <IonText>{(typeof displayValue === "string"? displayValue : JSON.stringify(displayValue)) || "---"}</IonText>
      </IonItem>
    );
  }

  switch (question.type) {
    case 'text':
      return <TextInput question={question.question} value={value} onChange={(v) => onChange(questionKey, v)} />;
    case 'number':
      return <NumberInput question={question.question} value={value} onChange={(v) => onChange(questionKey, v)} />;
    case 'date':
      return <DateInput question={question.question} value={value} onChange={(v) => onChange(questionKey, v)} />;
    case 'birthdate':
      return <BirthdateInput question={question.question} value={value} onChange={(v) => onChange(questionKey, v)} />;
    case 'multipleChoice':
      return <MultipleChoice question={question.question} options={question.options} value={value} multiple={question.multiple} onChange={(v) => onChange(questionKey, v)} />;
    case 'choiceNumber':
      return <ChoiceNumber question={question.question} options={question.options} value={value} onChange={(v) => onChange(questionKey, v)} />;
    case 'slider':
      return <SliderInput question={question.question} value={value} min={question.min} max={question.max} onChange={(v) => onChange(questionKey, v)} />;
    default:
      return null;
  }
};

// Interfaces for different input components
interface TextInputProps {
  question: string;
  value: string;
  onChange: (value: string) => void;
}

const TextInput: React.FC<TextInputProps> = ({ question, value, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <IonInput value={value} onIonChange={(e) => onChange(e.detail.value!)} />
  </IonItem>
);

interface NumberInputProps {
  question: string;
  value: number;
  onChange: (value: number) => void;
}

const NumberInput: React.FC<NumberInputProps> = ({ question, value, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <IonInput type="number" value={value} onIonChange={(e) => onChange(parseFloat(e.detail.value!))} />
  </IonItem>
);

interface MultipleChoiceProps {
  question: string;
  options: string[];
  value: string[] | string;
  multiple?: boolean;
  onChange: (value: string[] | string) => void;
}

const MultipleChoice: React.FC<MultipleChoiceProps> = ({ question, options, value, multiple, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <IonSelect multiple={multiple} value={value} onIonChange={(e) => onChange(e.detail.value)}>
      {options.map((option) => (
        <IonSelectOption key={option} value={option}>
          {option}
        </IonSelectOption>
      ))}
    </IonSelect>
  </IonItem>
);

interface ChoiceNumberProps {
  question: string;
  options: { label: string; value: number }[];
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

const ChoiceNumber: React.FC<ChoiceNumberProps> = ({ question, options, value, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <IonSelect value={value} onIonChange={(e) => onChange(e.detail.value)}>
      {options.map((option) => (
        <IonSelectOption key={option.label} value={option.value}>
          {option.label}
        </IonSelectOption>
      ))}
    </IonSelect>
  </IonItem>
);

interface SliderInputProps {
  question: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

const SliderInput: React.FC<SliderInputProps> = ({ question, value, min, max, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}: {value}</IonLabel>
    <IonRange min={min} max={max} value={value} onIonChange={(e) => onChange(e.detail.value as number)}/>
  </IonItem>
);

interface DateInputProps {
  question: string;
  value: string;
  onChange: (value: string) => void;
}

const DateInput: React.FC<DateInputProps> = ({ question, value, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <IonInput 
      type="date" 
      value={value} 
      onIonChange={(e) => onChange(e.detail.value!)} 
    />
  </IonItem>
);

interface BirthdateInputProps {
  question: string;
  value: Birthdate | undefined;
  onChange: (value: Birthdate | undefined) => void;
}

const BirthdateInput: React.FC<BirthdateInputProps> = ({ question, value, onChange }) => (
  <IonItem>
    <IonLabel position="stacked">{question}</IonLabel>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem', width: '100%' }}>
      <IonInput
        inputmode="numeric"
        placeholder="MM"
        value={value?.month ?? ''}
        onIonChange={(e) => {
          const month = Number(e.detail.value);
          if (!month) {
            onChange(undefined);
            return;
          }
          onChange({ year: value?.year ?? new Date().getFullYear(), month, day: value?.day ?? 1 });
        }}
      />
      <IonInput
        inputmode="numeric"
        placeholder="DD"
        value={value?.day ?? ''}
        onIonChange={(e) => {
          const day = Number(e.detail.value);
          if (!day) {
            onChange(undefined);
            return;
          }
          onChange({ year: value?.year ?? new Date().getFullYear(), month: value?.month ?? 1, day });
        }}
      />
      <IonInput
        inputmode="numeric"
        placeholder="YYYY"
        value={value?.year ?? ''}
        onIonChange={(e) => {
          const year = Number(e.detail.value);
          if (!year) {
            onChange(undefined);
            return;
          }
          onChange({ year, month: value?.month ?? 1, day: value?.day ?? 1 });
        }}
      />
    </div>
  </IonItem>
);